import { isStartBlocked, type StartFailure } from '@/features/ai/lib/parseStartError'
import { BLOCK_EDIT_PROMPT_MAX, MAX_PROMPT_LENGTH } from '@/features/ai/lib/promptLimits'
import type { SidebarMode } from './types'

interface Args {
  mode: SidebarMode
  isRunning: boolean
  /** Курс занят ЧУЖИМ прогоном. */
  isBusy: boolean
  isLost: boolean
  /** Курс на проверке (`useIsCourseFrozen`): бэк отбивает любую запись в
   *  контент, платный прогон уйти не должен. Гасит так же, как чужой прогон. */
  isFrozen: boolean
  /** Область правки (`useEditScope().count`) — 0 значит физически нечего
   *  отправлять, даже если промпт набран. */
  editScopeCount: number
  /** E8: в области правки есть блок с несохранённым черновиком
   *  (`useBlockDraftContext().isDirty` + его id входит в `useEditScope().blockIds`).
   *  Прогон переписал бы блок на сервере, а расхождение черновика с новым
   *  снимком (Task 7) заставило бы панель посчитать его «настоящей правкой
   *  автора» — клик по «Сохранить» стёр бы оплаченный результат. Гасим старт
   *  ДО него, а не разруливаем конфликт после. */
  hasUnsavedBlockInScope: boolean
  failure: StartFailure | null
  editFailure: StartFailure | null
  trimmedPromptLength: number
}

export interface ComposerGates {
  isInputDisabled: boolean
  canSubmit: boolean
  /** Отказ ТОГО режима, в котором он случился — генерации и правки блока
   *  подмешивать друг в друга нельзя (разные ручки, разные тела). */
  activeFailure: StartFailure | null
  promptMax: number
}

/**
 * Гейты композера — чистая функция (не хук: ни одной подписки), а не тело
 * `AISidebar` напрямую, по двум причинам: держит сложность компонента под
 * лимитом eslint и даёт риску №3 брифа («гейты „можно отправлять“ не должны
 * разъехаться») ЕДИНУЮ точку формул вместо россыпи переменных в компоненте.
 *
 * `generate` — ввод разрешён всегда (кроме общих гейтов ниже). `edit-block`
 * — разрешён, только пока область непуста (риск №2 брифа: снять `disabled` с
 * пилюли мало, поле обязано ожить вместе с ней). `idle` — поле всегда
 * гашёное (свободного чата у панели нет). Идёт прогон (свой или чужой) —
 * старт тоже гасим: двойной клик плодит два курса/две правки, а чужой прогон
 * отбил бы 409 стартом — гасим проактивно. `isLost`: плашка пропавшего
 * прогона стоит в теле панели выше настроек — живое поле под ней запускало
 * бы платный прогон вслепую.
 */
export const resolveComposerGates = ({
  mode,
  isRunning,
  isBusy,
  isLost,
  isFrozen,
  editScopeCount,
  hasUnsavedBlockInScope,
  failure,
  editFailure,
  trimmedPromptLength,
}: Args): ComposerGates => {
  const isModeEmpty = mode === 'idle' || (mode === 'edit-block' && editScopeCount === 0)
  const isInputDisabled = isModeEmpty || isRunning || isBusy || isLost || isFrozen
  // M-5: отказ показывается ТОЛЬКО в своём режиме. В `idle` (куда `submit`
  // уводит по подтверждённому старту) не показывается ни один: иначе
  // последовательность «генерация упала → ушёл в правку → правка стартовала»
  // оставляет под идущим прогоном правки баннер про генерацию.
  const activeFailure = mode === 'edit-block' ? editFailure : mode === 'generate' ? failure : null
  const promptMax = mode === 'edit-block' ? BLOCK_EDIT_PROMPT_MAX : MAX_PROMPT_LENGTH
  // E8: поле остаётся живым (автор может готовить промпт заранее), но
  // СТАРТ гасим — символ дефекта тот же, что и у `isModeEmpty`/`isStartBlocked`
  // выше: не давать платному прогону уйти, зная заранее, что он перепишет
  // блок, потерю правки которого сам же и вызовет.
  const isUnsavedEditBlocked = mode === 'edit-block' && hasUnsavedBlockInScope
  // I-3/правило C-D: `503 n8n_not_configured` — свойство окружения, а не
  // неудача клика: пока показан этот отказ, старт погашен (иначе каждый клик
  // = пустой прогон/пустая правка). Тот же гейт кормит и кнопку, и
  // «Повторить» в баннере отказа (I-1) — источник один.
  const canSubmit =
    !isInputDisabled &&
    !isUnsavedEditBlocked &&
    !isStartBlocked(activeFailure) &&
    trimmedPromptLength > 0 &&
    trimmedPromptLength <= promptMax
  return { isInputDisabled, canSubmit, activeFailure, promptMax }
}
